// La macchina a stati della cascata nel browser e il riepilogo della sessione (piano M5, D5 e D6; i 3 secondi prima di ogni
// chiamata sono la proposta del 26 settembre 2026, confermata il 28). Funzioni pure e senza orologio: il tempo lo dà
// l'evento `tick`, uno al secondo. Il server resta la verità della sessione (riserva, avanzamento, conteggi): questa
// macchina dice soltanto che cosa mostra e che cosa permette la barra «Power Dialer» per il Lead aperto.
//
// Dopo la chiamata il Lead successivo arriva da solo (decisione del product owner del 9 ottobre 2026): con la postazione
// chiusa («Chiudi la chiamata», che riesce soltanto a passi fatti) parte un secondo conto, `advanceLeft`, con i secondi
// dell'impostazione «Dopo la chiamata, passa al Lead successivo» (subito, 3, 5 o 10; `null` per «lo passo io»). Lo stesso
// conto vale per il Lead che si salta da solo. A zero il browser avanza con il comando di «Lead successivo»; «Resta su questo
// Lead» lo toglie, «Pausa» lo ferma, una chiamata nuova allo stesso Lead lo toglie.
//
// Senza risposta il Lead si richiama da solo (decisione del product owner dell'11 ottobre 2026, TEL-32): con l'esito «Nessuna
// risposta» (di Gioia365 o scelto a mano, con la postazione chiusa) e altre chiamate di fila nel tentativo, il browser manda
// `redial` e il Lead torna al conto alla rovescia di sempre, con `redial` che dice quale chiamata di fila è. A zero la chiamata
// parte come sempre; «Salta» è spento, perché il Lead è già stato chiamato.
/**
 * Lo stato di un Lead appena aperto. Un Lead che si salta da solo parte `passed`, con il conto verso il Lead successivo
 * (`advanceSeconds`, `null` per «lo passo io»); senza secondi di attesa («niente, chiama subito») non c'è conto alla rovescia
 * e la chiamata parte subito (`calling`). `countdownSeconds` è l'impostazione dell'azienda, già validata da 0 a 10: un valore
 * non intero si arrotonda per difetto e uno negativo vale 0.
 */
export function initialDialerState(countdownSeconds, passover, advanceSeconds = null) {
    if (passover)
        return { phase: "passed", secondsLeft: 0, pausedFrom: null, advanceLeft: advanceOf(advanceSeconds), redial: null };
    const seconds = wholeSeconds(countdownSeconds);
    if (seconds === 0)
        return { phase: "calling", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
    return { phase: "countdown", secondsLeft: seconds, pausedFrom: null, advanceLeft: null, redial: null };
}
/** I secondi dell'impostazione: un valore non intero si arrotonda per difetto, uno negativo o che non è un numero vale 0. */
function wholeSeconds(countdownSeconds) {
    return Number.isFinite(countdownSeconds) ? Math.max(0, Math.floor(countdownSeconds)) : 0;
}
/** Il conto verso il Lead successivo dall'impostazione: `null` per «lo passo io», altrimenti i secondi interi. */
function advanceOf(advanceSeconds) {
    return advanceSeconds === null ? null : wholeSeconds(advanceSeconds);
}
/**
 * Il passo di fase `from` → `to`, sia da sola sia sotto pausa: in pausa il Lead aperto si finisce (la chiamata finisce,
 * l'esito si salva) e `pausedFrom` segue, così «Riprendi» torna alla fase giusta. Ogni fase di arrivo non ha secondi, né il
 * conto verso il Lead successivo, né il richiamo: li mette chi li fa partire.
 */
function moveFrom(state, from, to) {
    if (state.phase === from)
        return { phase: to, secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
    if (state.phase === "paused" && state.pausedFrom === from)
        return { phase: "paused", secondsLeft: 0, pausedFrom: to, advanceLeft: null, redial: null };
    return state;
}
export function reduceDialerState(state, event) {
    switch (event.type) {
        case "tick":
            // Il conto alla rovescia prima della chiamata (e quello del richiamo, che è lo stesso), e quello verso il Lead successivo dopo
            // la chiamata (si ferma a zero: allo zero avanza il browser, con il comando di «Lead successivo»). In pausa tutto è fermo.
            if ((state.phase === "done" || state.phase === "passed") && state.advanceLeft !== null && state.advanceLeft > 0)
                return { ...state, advanceLeft: state.advanceLeft - 1 };
            if (state.phase !== "countdown")
                return state;
            return state.secondsLeft > 1
                ? { ...state, secondsLeft: state.secondsLeft - 1 }
                : { phase: "calling", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
        case "call_now":
            // «Chiama subito» è un comando: vale nel conto alla rovescia, non in pausa.
            return state.phase === "countdown" ? { phase: "calling", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null } : state;
        case "call_started": {
            // Una chiamata partita è un fatto del server e vince: anche sotto pausa, perché alla ripresa non si richiami.
            // Dopo l'esito vale «Richiama ora», la seconda chiamata allo stesso Lead, e il conto verso il Lead successivo si toglie.
            if (state.phase === "calling")
                return state;
            if (state.phase === "paused")
                return state.pausedFrom === "calling" ? state : { phase: "paused", secondsLeft: 0, pausedFrom: "calling", advanceLeft: null, redial: null };
            return { phase: "calling", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
        }
        case "call_ended":
            return moveFrom(state, "calling", "outcome");
        case "outcome_saved":
            // L'esito salvato con i passi fatti a postazione ancora aperta: «Lead successivo» si accende, ma il conto non parte (chi
            // scrive una nota non viene portato via).
            return moveFrom(state, "outcome", "done");
        case "desk_closed": {
            // «Chiudi la chiamata» (riesce soltanto a passi fatti): il Lead è fatto e parte il conto verso il successivo
            // (decisione del product owner del 9 ottobre 2026), anche sotto pausa, dove resta fermo.
            const advanceLeft = advanceOf(event.advanceSeconds);
            const done = moveFrom(state, "outcome", "done");
            if (done.phase === "done")
                return { ...done, advanceLeft };
            if (done.phase === "paused" && done.pausedFrom === "done")
                return { ...done, advanceLeft };
            return done;
        }
        case "stay":
            // Toglie il conto verso il Lead successivo e, nel richiamo, il richiamo stesso: il Lead torna a «fatto» (sotto pausa resta
            // in pausa, da «fatto»), perché un comando che non riesce non lasci ripartire il conto e far partire da solo una chiamata
            // vera (TEL-32). Il conto alla rovescia di un Lead non ancora chiamato non cambia.
            if (state.redial !== null)
                return state.phase === "paused"
                    ? { phase: "paused", secondsLeft: 0, pausedFrom: "done", advanceLeft: null, redial: null }
                    : { phase: "done", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null };
            return state.advanceLeft === null ? state : { ...state, advanceLeft: null };
        case "lead_passed": {
            // Un Lead già in chiamata o già chiamato resta aperto: lo si finisce (D1). Saltato, segue il conto verso il successivo.
            const passed = moveFrom(state, "countdown", "passed");
            return passed === state ? state : { ...passed, advanceLeft: advanceOf(event.advanceSeconds) };
        }
        case "call_failed": {
            // Dalla chiamata (anche sotto pausa, anche dopo «Chiudi la chiamata» con la chiamata mai arrivata al Lead) al conto alla
            // rovescia in pausa: «Riprendi» lo fa ripartire intero. Almeno un secondo, per saltare o rimettere in pausa.
            const inCall = (phase) => phase === "calling" || phase === "outcome";
            if (!inCall(state.phase) && !(state.phase === "paused" && inCall(state.pausedFrom)))
                return state;
            return { phase: "paused", secondsLeft: Math.max(1, wholeSeconds(event.countdownSeconds)), pausedFrom: "countdown", advanceLeft: null, redial: null };
        }
        case "redial": {
            // Senza risposta e con altre chiamate di fila nel tentativo: lo stesso Lead, dopo i secondi prima della chiamata (decisione
            // dell'11 ottobre 2026). Senza secondi la chiamata parte subito; sotto pausa resta in pausa, con almeno un secondo da riprendere.
            const redial = { call: event.call, of: event.of };
            const seconds = wholeSeconds(event.countdownSeconds);
            if (state.phase === "outcome" || state.phase === "done")
                return seconds === 0
                    ? { phase: "calling", secondsLeft: 0, pausedFrom: null, advanceLeft: null, redial: null }
                    : { phase: "countdown", secondsLeft: seconds, pausedFrom: null, advanceLeft: null, redial };
            if (state.phase === "paused" && (state.pausedFrom === "outcome" || state.pausedFrom === "done"))
                return { phase: "paused", secondsLeft: Math.max(1, seconds), pausedFrom: "countdown", advanceLeft: null, redial };
            return state;
        }
        case "pause":
            // La pausa tiene i secondi che restano, anche quelli verso il Lead successivo e il richiamo: «Riprendi» riparte da lì.
            return state.phase === "paused"
                ? state
                : { phase: "paused", secondsLeft: state.secondsLeft, pausedFrom: state.phase, advanceLeft: state.advanceLeft, redial: state.redial };
        case "resume":
            return state.phase === "paused" && state.pausedFrom !== null
                ? { phase: state.pausedFrom, secondsLeft: state.secondsLeft, pausedFrom: null, advanceLeft: state.advanceLeft, redial: state.redial }
                : state;
    }
}
/** Il conto verso il Lead successivo scorre (o è arrivato a zero): dopo la chiamata chiusa, o per un Lead che si salta da solo. */
export function isAdvancing(state) {
    return (state.phase === "done" || state.phase === "passed") && state.advanceLeft !== null;
}
/**
 * «Lead successivo» si accende dopo l'esito, per un Lead che si salta da solo e, nel conto alla rovescia, come «passato al
 * successivo senza chiamare». Resta acceso anche sotto una pausa partita da un Lead già fatto (`done`) o che si salta da solo
 * (`passed`): la pausa non ha più niente da fermare su quel Lead, e il browser riprende la sessione prima di avanzare. Nella prova
 * del 9 ottobre 2026 «Pausa» premuto a chiamata finita, e poi il ricaricamento, lasciavano «Lead successivo» spento mentre la
 * postazione diceva «Fatto tutto: passa al Lead successivo» (il product owner ha detto «ok» a tenerlo acceso). Dalla pausa del
 * conto alla rovescia, della chiamata o dell'esito resta spento: lì il Lead non è finito e si riprende prima. Fa eccezione la
 * pausa del conto del richiamo (`redial` non nullo): il Lead è già stato chiamato, e per la regola TEL-27 («In pausa su un Lead
 * già chiamato … «Lead successivo» resta acceso») riprende la sessione e apre il Lead dopo.
 */
export function canAdvance(state) {
    if (state.phase === "paused")
        return state.pausedFrom === "done" || state.pausedFrom === "passed" || (state.pausedFrom === "countdown" && state.redial !== null);
    return state.phase === "done" || state.phase === "passed" || state.phase === "countdown";
}
/** Il conto alla rovescia è il richiamo dello stesso Lead (TEL-32), anche sotto pausa: «Salta» spento, «Lead successivo» con «Decidi tu». */
export function isRedialing(state) {
    return state.redial !== null;
}
/** «Salta» si accende soltanto nel conto alla rovescia di un Lead non ancora chiamato: mai nel richiamo (TEL-32). */
export function canSkip(state) {
    return state.phase === "countdown" && state.redial === null;
}
/**
 * Il riepilogo di «Sessione interrotta» e della fine della lista (D6): un calcolo sulle righe che esistono già. `items`
 * sono i Lead lavorati nella sessione con le loro chiamate; `queue` dice quanti restano e quanti si saltano da soli,
 * dalla coda riletta. La durata di una chiamata senza risposta (il tempo di squillo) non è tempo al telefono. Una risposta
 * senza durata vale 0 secondi: il dizionario dati (`CRM_DATA_DICTIONARY.md`) vuole `null` per un dato incompleto, e qui il
 * tipo del riepilogo è un numero (ipotesi dichiarata, questione 254).
 */
export function summarize(items, queue) {
    const summary = {
        leadsCalled: 0,
        calls: 0,
        answered: 0,
        talkSeconds: 0,
        skippedByUser: 0,
        passedWithoutCall: 0,
        passedOver: queue.passedOver,
        remaining: queue.remaining,
    };
    for (const item of items) {
        if (item.result === "called")
            summary.leadsCalled += 1;
        else if (item.result === "skipped")
            summary.skippedByUser += 1;
        else
            summary.passedWithoutCall += 1;
        for (const call of item.calls) {
            summary.calls += 1;
            if (!call.answered)
                continue;
            summary.answered += 1;
            if (call.durationSeconds !== null && Number.isFinite(call.durationSeconds) && call.durationSeconds > 0)
                summary.talkSeconds += call.durationSeconds;
        }
    }
    return summary;
}
//# sourceMappingURL=dialer-session.js.map